import { useState, useCallback, useEffect } from 'react';
import VerticalSLD from './VerticalSLD';
import SLDDetailPanel from './SLDDetailPanel';
import { useSLDTelemetry } from './hooks/useSLDTelemetry';
import { mapMetricsToNodeData } from './utils/metricMapper';
import type { SLDConfig, SLDSelectedNode } from './utils/sldTypes';

interface SLDTabProps {
  siteId: string;
  sldConfig: SLDConfig;
  onNavigate: (tab: string, filter?: Record<string, string>) => void;
  onDeviceSelect?: (node: SLDSelectedNode | null) => void;
}

export default function SLDTab({ siteId, sldConfig, onNavigate, onDeviceSelect }: SLDTabProps) {
  const telemetry = useSLDTelemetry(siteId, true);
  const [selectedNode, setSelectedNode] = useState<SLDSelectedNode | null>(null);

  const handleNodeSelect = useCallback((node: SLDSelectedNode | null) => {
    setSelectedNode(node);
    onDeviceSelect?.(node);
  }, [onDeviceSelect]);

  // Keep selected node data live when telemetry updates
  useEffect(() => {
    if (!selectedNode || !telemetry?.devices) return;
    const deviceId = selectedNode.data.deviceId as string | undefined;
    if (!deviceId || !telemetry.devices[deviceId]) return;

    const liveData = mapMetricsToNodeData(telemetry.devices[deviceId], selectedNode.data);
    setSelectedNode(prev => prev ? { ...prev, data: liveData } : null);
  }, [telemetry]);

  const handleViewDetails = useCallback((assetType: string) => {
    onNavigate('Overview', { assetType });
  }, [onNavigate]);

  const handleClose = useCallback(() => {
    setSelectedNode(null);
    onDeviceSelect?.(null);
  }, [onDeviceSelect]);

  return (
    <div className="relative w-full" style={{ height: 'calc(100vh - 220px)' }}>
      <VerticalSLD
        siteId={siteId}
        config={sldConfig}
        telemetry={telemetry}
        onNodeSelect={handleNodeSelect}
      />
      <SLDDetailPanel
        siteId={siteId}
        selectedNode={selectedNode}
        onClose={handleClose}
        onViewDetails={handleViewDetails}
      />
    </div>
  );
}
